import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HelpCircle, Zap, Flame, FileCheck, Trash2 } from "lucide-react";
import { Badge } from "@/components/Badge";
import { ReadinessBadge } from "@/components/ReadinessBadge";
import { STAGES, STAGE_COLORS, isBusiness, clienteExtra, fornituraPratica } from "@/lib/constants";
import { getPraticheAgenzia, deletePraticaAgenzia } from "@/api/agenzia";
import { ApiError } from "@/api/client";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { formatDateTime } from "@/lib/dates";
import type { ContrattoAffiliato } from "@/types";
import { ErrorState } from "@/components/ErrorState";
import { OnboardingTour } from "@/onboarding/OnboardingTour";
import { praticheListSteps, praticheListVersion } from "@/onboarding/praticheListSteps";
import { useOnboarding } from "@/onboarding/useOnboarding";
import { useAuth } from "@/auth/useAuth";
import { useListView } from "@/hooks/useListView";
import { ListViewToggle } from "@/components/ListViewToggle";

// Righe mock mostrate SOLO durante il tour su pagina vuota, così il bot
// ha qualcosa da evidenziare negli step "apri dettaglio" / "paginazione".
// Non sono pratiche reali — nessun click.
const MOCK_PRATICHE: ContrattoAffiliato[] = [
  {
    id: "__mock_1__",
    name: "Esempio — Cambio Luce Rossi",
    stage: "Bozza",
    close_date: null,
    created_at: new Date().toISOString(),
    amount: null,
    cliente_name: "Mario Rossi (esempio)",
    cliente_id: null,
    quota_agenzia: 25,
  },
  {
    id: "__mock_2__",
    name: "Esempio — Voltura Gas Bianchi",
    stage: "In lavorazione",
    close_date: null,
    created_at: new Date().toISOString(),
    amount: null,
    cliente_name: "Lucia Bianchi (esempio)",
    cliente_id: null,
    quota_agenzia: 30,
  },
];

const PAGE_SIZE = 20;

export function PraticheAgenzia() {
  const navigate = useNavigate();
  const [view, setView] = useListView("pratiche");
  const [items, setItems] = useState<ContrattoAffiliato[]>([]);
  const [total, setTotal] = useState(0);
  const [autoAttivo, setAutoAttivo] = useState(false);
  const [page, setPage] = useState(0);
  const [stageFilter, setStageFilter] = useState<string>("");
  const [search, setSearch] = useState("");
  const [pendingSearch, setPendingSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mostra righe di esempio solo durante il tour onboarding se la tabella è vuota:
  // altrimenti gli step "apri dettaglio" / "paginazione" puntano a elementi mancanti.
  // `tourJustFinished`: flag locale per nascondere mock immediatamente alla fine
  // del tour, senza aspettare che markSeen async aggiorni il context user.
  const { seenVersion } = useOnboarding("pratiche_list");
  const tourSeen = seenVersion >= praticheListVersion;
  const [tourJustFinished, setTourJustFinished] = useState(false);
  const [replayActive, setReplayActive] = useState(false);
  const [replayNonce, setReplayNonce] = useState(0);
  const { hasRole } = useAuth();
  const isStaff = hasRole("affiliato") || hasRole("operatore");
  const canDeleteRole = hasRole("affiliato") || hasRole("operatore_supervisore");
  const [toDelete, setToDelete] = useState<ContrattoAffiliato | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function handleDelete() {
    if (!toDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deletePraticaAgenzia(toDelete.id);
      setItems((prev) => prev.filter((p) => p.id !== toDelete.id));
      setTotal((t) => Math.max(0, t - 1));
      setToDelete(null);
    } catch (e) {
      setDeleteError(
        e instanceof ApiError && e.detail ? e.detail : "Impossibile cancellare la pratica.",
      );
    } finally {
      setDeleting(false);
    }
  }

  // Mock visibile: primo tour (non ancora seen) oppure replay esplicito,
  // solo se tabella vuota e non sto caricando/in errore.
  const tourIsActive = (!tourSeen && !tourJustFinished) || replayActive;
  const showMock =
    tourIsActive && !loading && !error && items.length === 0;
  const rowsToRender = showMock ? MOCK_PRATICHE : items;
  // Il tour aggancia i suoi riquadri alla PRIMA riga utile, non a tutte:
  // la targhetta dell'invio alla prima pratica, il cestino alla prima bozza.
  const primaRigaId = rowsToRender[0]?.id;
  const primaBozzaId = canDeleteRole
    ? rowsToRender.find((c) => c.stage === "Bozza")?.id
    : undefined;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPraticheAgenzia({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      stage: stageFilter || null,
      q: search || undefined,
    })
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
        setAutoAttivo(res.invio_automatico_attivo ?? false);
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) setError("Impossibile caricare le pratiche.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, stageFilter, search]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    setSearch(pendingSearch.trim());
    setPage(0);
  }

  return (
    <div className="space-y-4">
      {/* Montato a elenco caricato: i passi si agganciano alle righe, che prima
          del caricamento non ci sono ancora. */}
      {!loading && (
        <OnboardingTour
          steps={praticheListSteps}
          flagKey="pratiche_list"
          version={praticheListVersion}
          replayNonce={replayNonce}
          onClose={() => {
            setTourJustFinished(true);
            setReplayActive(false);
          }}
        />
      )}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-text">Pratiche</h2>
          {isStaff && (
            <button
              type="button"
              onClick={() => {
                setTourJustFinished(false);
                setReplayActive(true);
                setReplayNonce((n) => n + 1);
              }}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-text transition-colors"
              title="Rivedi guida"
            >
              <HelpCircle size={14} />
              <span>Rivedi guida</span>
            </button>
          )}
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <ListViewToggle view={view} onChange={setView} />
          <form onSubmit={submitSearch} className="flex gap-2" data-tour="pratiche-search">
            <input
              type="text"
              value={pendingSearch}
              onChange={(e) => setPendingSearch(e.target.value)}
              placeholder="Cerca..."
              className="flex-1 px-3 py-2 border border-border rounded-lg text-sm bg-card min-w-0"
            />
            <button
              type="submit"
              className="px-3 py-2 bg-brand text-white rounded-lg text-sm flex-shrink-0"
            >
              Cerca
            </button>
          </form>
          <select
            value={stageFilter}
            onChange={(e) => {
              setStageFilter(e.target.value);
              setPage(0);
            }}
            data-tour="pratiche-stage-filter"
            className="px-3 py-2 border border-border rounded-lg text-sm bg-card"
          >
            <option value="">Tutti gli stati</option>
            {[...STAGES].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error ? (
        <ErrorState message={error} />
      ) : loading ? (
        <p className="text-sm text-muted-foreground">Caricamento...</p>
      ) : rowsToRender.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nessuna pratica.</p>
      ) : (
        <div className={view === "card" ? "" : "bg-card rounded-lg border border-border overflow-hidden"} data-tour="pratiche-table">
          {showMock && (
            <div className="px-3 py-2 text-xs text-muted-foreground bg-amber-50 border-b border-amber-200">
              Righe di esempio — i dati mostrati sono fittizi, servono solo per il tour guidato.
            </div>
          )}
          {/* Mobile sempre a card; su desktop card solo se vista=card (altrimenti tabella) */}
          <div className={view === "card" ? "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" : "sm:hidden divide-y divide-border"}>
            {rowsToRender.map((c) => {
              const color = STAGE_COLORS[c.stage] ?? STAGE_COLORS["Bozza"];
              const isMock = showMock;
              const fornitura = fornituraPratica(c.tipo, c.name);
              return (
                <div
                  key={c.id}
                  onClick={isMock ? undefined : () => navigate(`/agenzia/pratiche/${c.id}`)}
                  role={isMock ? undefined : "button"}
                  tabIndex={isMock ? undefined : 0}
                  onKeyDown={
                    isMock
                      ? undefined
                      : (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigate(`/agenzia/pratiche/${c.id}`);
                          }
                        }
                  }
                  className={(view === "card" ? "bg-card border border-border rounded-xl " : "") + (isMock ? "p-3 opacity-70" : "p-3 cursor-pointer hover:bg-surface transition-colors")}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-surface flex items-center justify-center flex-shrink-0 mt-0.5">
                        {fornitura === "Dual" ? (
                          <span className="flex items-center gap-px"><Zap size={11} className="text-amber-500" /><Flame size={11} className="text-orange-500" /></span>
                        ) : fornitura === "Luce" ? <Zap size={14} className="text-amber-500" /> : fornitura === "Gas" ? <Flame size={14} className="text-orange-500" /> : <FileCheck size={14} className="text-muted-foreground" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="text-sm font-medium text-text truncate">{c.name}</span>
                          {isBusiness(c.tipo_soggetto) && (
                            <Badge bg="bg-indigo-100" text="text-indigo-800">Business</Badge>
                          )}
                        </div>
                        {clienteExtra(c.name, c.cliente_name) && (
                          <div className="text-xs text-muted-foreground mt-0.5 truncate" title={c.cliente_name ?? undefined}>
                            {c.cliente_name}
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground mt-0.5">{formatDateTime(c.created_at)}</div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <Badge bg={color.bg} text={color.text}>{c.stage}</Badge>
                      {!isMock && (
                        <span data-tour={c.id === primaRigaId ? "pratiche-stato-invio-card" : undefined}>
                          <ReadinessBadge readiness={c.readiness} stage={c.stage} autoAttivo={autoAttivo} />
                        </span>
                      )}
                      {c.quota_agenzia != null && (
                        <span className="text-xs font-medium text-text">
                          €{c.quota_agenzia.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                        </span>
                      )}
                      {!isMock && canDeleteRole && c.stage === "Bozza" && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setToDelete(c); setDeleteError(null); }}
                          data-tour={c.id === primaBozzaId ? "pratiche-cestino-card" : undefined}
                          className="text-red-600 hover:text-red-700 mt-1"
                          title="Cancella pratica"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Desktop: table */}
          <table className={view === "card" ? "hidden" : "hidden sm:table w-full text-sm"}>
            <thead className="bg-surface text-muted-foreground">
              <tr>
                <th className="text-left py-2 px-3 font-medium">Data</th>
                <th className="text-left py-2 px-3 font-medium">Pratica</th>
                <th className="text-left py-2 px-3 font-medium">Stato</th>
                <th className="text-right py-2 px-3 font-medium">Commissione</th>
                <th className="text-right py-2 px-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {rowsToRender.map((c) => {
                const color = STAGE_COLORS[c.stage] ?? STAGE_COLORS["Bozza"];
                const isMock = showMock;
                const fornitura = fornituraPratica(c.tipo, c.name);
                return (
                  <tr
                    key={c.id}
                    onClick={isMock ? undefined : () => navigate(`/agenzia/pratiche/${c.id}`)}
                    role={isMock ? undefined : "button"}
                    tabIndex={isMock ? undefined : 0}
                    onKeyDown={
                      isMock
                        ? undefined
                        : (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              navigate(`/agenzia/pratiche/${c.id}`);
                            }
                          }
                    }
                    className={
                      isMock
                        ? "border-t border-border opacity-70"
                        : "border-t border-border cursor-pointer hover:bg-surface transition-colors"
                    }
                  >
                    <td className="py-2 px-3 text-text">{formatDateTime(c.created_at)}</td>
                    <td className="py-2 px-3 text-text">
                      <div className="flex items-start gap-2">
                        <div className="w-7 h-7 rounded-lg bg-surface flex items-center justify-center flex-shrink-0">
                          {fornitura === "Dual" ? (
                            <span className="flex items-center gap-px"><Zap size={10} className="text-amber-500" /><Flame size={10} className="text-orange-500" /></span>
                          ) : fornitura === "Luce" ? <Zap size={13} className="text-amber-500" /> : fornitura === "Gas" ? <Flame size={13} className="text-orange-500" /> : <FileCheck size={13} className="text-muted-foreground" />}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span>{c.name}</span>
                            {isBusiness(c.tipo_soggetto) && (
                              <Badge bg="bg-indigo-100" text="text-indigo-800">Business</Badge>
                            )}
                          </div>
                          {clienteExtra(c.name, c.cliente_name) && (
                            <div className="text-xs text-muted-foreground mt-0.5">{c.cliente_name}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex flex-col items-start gap-1">
                        <Badge bg={color.bg} text={color.text}>{c.stage}</Badge>
                        {!isMock && (
                          <span data-tour={c.id === primaRigaId ? "pratiche-stato-invio-riga" : undefined}>
                            <ReadinessBadge readiness={c.readiness} stage={c.stage} autoAttivo={autoAttivo} />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-right text-text">
                      {c.quota_agenzia != null
                        ? `€${c.quota_agenzia.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`
                        : "—"}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {!isMock && canDeleteRole && c.stage === "Bozza" && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setToDelete(c); setDeleteError(null); }}
                          data-tour={c.id === primaBozzaId ? "pratiche-cestino-riga" : undefined}
                          className="text-red-600 hover:text-red-700"
                          title="Cancella pratica"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between text-sm" data-tour="pratiche-pagination">
        <span className="text-muted-foreground">
          Pagina {page + 1} di {totalPages} — {total} totali
        </span>
        <div className="flex gap-2">
          <button
            disabled={page === 0 || loading}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-3 py-1 border border-border rounded disabled:opacity-50"
          >
            Precedente
          </button>
          <button
            disabled={page + 1 >= totalPages || loading}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 border border-border rounded disabled:opacity-50"
          >
            Successiva
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={toDelete !== null}
        title="Cancellare la pratica?"
        message={`Sei sicuro? L'azione non è reversibile.${toDelete ? ` (${toDelete.name})` : ""}`}
        confirmLabel="Cancella"
        busy={deleting}
        error={deleteError}
        onConfirm={handleDelete}
        onCancel={() => { setToDelete(null); setDeleteError(null); }}
      />
    </div>
  );
}
